/**
 * AI Analyst copy: the page, the conversation, and the words the pure helpers
 * in `components/AiAnalyst/utils.ts` produce.
 *
 * Ported verbatim from VentaHub (`pages/AiAnalyst/*`) so prompts, evals and
 * docs that quote the UI still match. The sibling modules carry the rest and
 * are wired under this one in `en.ts`:
 * `aiAnalystParts` (answer parts), `aiAnalystMessage` (message chrome),
 * `aiAnalystComposer`, `aiAnalystHistory` (sidebar, reports, memory)
 * and `aiAnalystShell` (launcher and panel).
 */
export const aiAnalyst = {
  title: 'AI Analyst',
  welcome: {
    overline: 'AI Analyst · sales, ads, stock and payouts',
    heading: 'Ask your AI Analyst',
    intro:
      'It investigates your sales, advertising, inventory, payouts and operations across every marketplace, explains what it finds and shows the numbers. Ask a question, or generate a snapshot of the business.',
  },
  tips: {
    button: 'Tips',
    snapshotTitle: 'Snapshot or Deep analysis?',
    snapshotBody:
      'A snapshot is a full business health review with prioritised insights (a few minutes). Deep analysis answers your one question more thoroughly with the stronger model.',
    focusTitle: 'Focus it',
    focusBody:
      'Scope narrows a conversation to some stores, brands or a period. Attach limits an answer to a list of SKUs.',
    keepGoingTitle: 'Keep going',
    keepGoingBody:
      'Ask follow-ups ("now by store"), use the suggested questions under an answer, or edit and retry your last question.',
    checkTitle: 'Check the numbers',
    checkBody:
      '"How this was calculated" under an answer shows the period, definitions and data freshness behind it.',
    anyPageTitle: 'From any page',
    // VentaHub's button sat in the header; VentaWiz's floats on every page.
    anyPageBody: 'Use "Ask AI" to ask about the page you are on.',
  },
  tryAsking: {
    heading: 'Try asking',
    tabsLabel: 'Example questions by topic',
  },
  topics: {
    sales: 'Sales',
    ads: 'Ads',
    listings: 'Listings',
    stock: 'Stock',
    payouts: 'Payouts',
    ops: 'Ops',
  },
  // "Try asking", worded for the organisation's first marketplace.
  examples: {
    defaultMarketplace: 'Amazon',
    sales: {
      weekOverWeek: 'How did {{marketplace}} sales do last week versus the week before?',
      growersDroppers: 'Which SKUs grew or dropped the most this month, and why?',
      marginByStore: 'What is my gross margin by store for last month?',
      driveMoreSales: 'Which SKUs can drive more sales, and what actions should I take?',
      projectedSku:
        'What are the projected SKU-level sales for next month, with stock and ads considered?',
      projectedChannel:
        'What are the projected sales by channel for next month, with stock and ads considered?',
    },
    ads: {
      wastedSpend: 'Which campaigns wasted the most spend in the last 14 days?',
      acosTacos: 'What were my ACOS and TACoS by store last month?',
      adGroupsExcess: 'Which ad groups are causing excess spend?',
      targetsExcess: 'Which targets are causing excess spend?',
      targetsIncrease: 'Which targets should I increase spend on to get more sales?',
      targetsReduce: 'Which targets should I reduce bids on to cut spend?',
      strategy: 'What marketing strategy should I follow to grow my top SKUs?',
      slotShare: 'What share of sponsored search slots do we win on our focus search terms?',
    },
    listings: {
      competitorAsins:
        'Get me the top 5 competitor ASINs winning sponsored search slots on our search terms',
      topCompetitors: 'Who are the top competitors against our SKUs and targets?',
      pricedAbove: 'Which top SKUs are priced above competitors?',
      rightPrice: "What's the right price to sell my top SKUs?",
      ratingIssues: 'Which top SKUs have rating or review issues?',
      catalogueIssues: 'Which top SKUs (by stock and sales) have catalogue issues?',
    },
    stock: {
      runOut: 'Which of my best sellers will run out of stock in the next 2 weeks?',
      unsellable: 'How much {{marketplace}} warehouse stock is unsellable or aged?',
      inTransit: 'What is in transit from vendors, and when does it arrive?',
      notSupplied: 'Which top SKUs were ordered but not supplied?',
      oosLoss: 'Which SKUs have the highest sales loss from being out of stock?',
    },
    payouts: {
      paidOut: 'How much was paid out on {{marketplace}} last month after fees?',
      biggestFees: 'What are my biggest fees by marketplace?',
      unsettled: 'How much revenue has not been settled yet?',
    },
    ops: {
      behindSchedule: 'Which purchase orders are behind schedule?',
      lateVendors: 'Which vendors deliver late or short?',
      slaBreaches: 'How many tickets are breaching their SLA right now?',
    },
  },
  page: {
    openHistory: 'Open history',
    startNew: 'Start a new conversation',
    loadingConversation: 'Loading conversation',
    notAvailable:
      'This conversation is not available. It may have been deleted, or it belongs to another organization.',
    debug: 'Debug',
    debugLabel: 'Debug mode',
    debugTooltip: 'Super admins only: show the SQL, calls, reasoning and usage behind every answer',
  },
  chat: {
    newConversation: 'New conversation',
    share: 'Share',
    stopSharing: 'Stop sharing',
    shareTooltip: 'Share a read-only link',
    sharedTooltip: 'Shared: click to stop sharing',
    linkCopied: 'Link copied. Anyone with AI Analyst access to this organization can read it.',
    sharingOff: 'Sharing turned off.',
    sharingFailed: "Couldn't change sharing. Try again.",
    exportConversation: 'Export conversation',
    downloadWord: 'Download Word',
    downloadMarkdown: 'Download Markdown',
    exportFailed: 'Export failed.',
    jumpToLatest: 'Jump to latest',
    reattach: 'Reattach',
    close: 'Close',
    backInTheBox: 'Your question is back in the box.',
    limitResets: '{{resetsIn}} (midnight {{zone}}).',
  },
  // One message (MessageView).
  messageView: {
    analyst: 'AI Analyst',
    editQuestion: 'Edit question',
    editYourQuestion: 'Edit your question',
    cancel: 'Cancel',
    saveAndAsk: 'Save and ask',
    inThisAnswer: 'In this answer',
    notifyNote: "You can leave this page. You'll get a notification when the snapshot is ready.",
    digDeeper: 'Dig deeper',
    noAnswer: 'No answer was produced.',
    tryAgain: 'Try again',
  },
  shared: {
    title: 'Shared conversation',
    sharedBy: 'Shared by {{owner}} · read-only',
    aColleague: 'a colleague',
    youAreSharing: 'You are sharing this conversation · read-only view',
    startOwn: 'Start your own conversation',
    openMine: 'Open my conversation',
    notAvailable:
      'This conversation is not available. It may no longer be shared, or it belongs to another organization.',
  },
  // Sidebar groups (`groupConversations`).
  groups: {
    pinned: 'Pinned',
    today: 'Today',
    previous7Days: 'Previous 7 days',
    older: 'Older',
  },
  // Scope pins as chips (`pinChips`).
  pins: {
    stores_one: '{{count}} store',
    stores_other: '{{count}} stores',
    brands_one: '{{count}} brand',
    brands_other: '{{count}} brands',
    range: '{{from}} – {{to}}',
    sameMonthRange: '{{fromDay}}–{{to}}',
    from: 'From {{day}}',
    until: 'Until {{day}}',
    dayWithYear: '{{day}} {{year}}',
  },
  timing: {
    seconds: 'usually about {{seconds}} s',
    minutes: 'usually about {{minutes}} min',
  },
  status: {
    working: 'Working',
    connecting: 'Connecting',
    ellipsis: '{{label}}…',
  },
  chart: {
    line: 'Line',
    bar: 'Bar',
    area: 'Area',
    pie: 'Pie',
    other: 'Other',
    description_one: '{{kind}} chart of {{series}} by {{x}}, {{count}} point.',
    description_other: '{{kind}} chart of {{series}} by {{x}}, {{count}} points.',
  },
  cell: {
    yes: 'Yes',
    no: 'No',
  },
  // The answer as Markdown (`answerMarkdown`).
  markdown: {
    result: 'Result',
    chart: 'Chart',
    chartData: '{{title}} (data)',
    insights: 'Insights',
    priority: 'Priority',
    area: 'Area',
    finding: 'Finding',
    doNext: 'Do next',
  },
  severity: {
    critical: 'Critical',
    important: 'Important',
    opportunity: 'Opportunity',
  },
  confidence: {
    veryLow: 'Very low',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    veryHigh: 'Very high',
  },
  usage: {
    resetsInHours: 'Resets in {{hours}}h {{minutes}}m',
    resetsInMinutes: 'Resets in {{minutes}}m',
  },
  errors: {
    generic: 'Something went wrong. Please try again.',
    requestId: 'Request ID: {{id}}',
  },
} as const
